import React, { useRef, useEffect } from "react";
import PrintIcon from "@mui/icons-material/Print";
import { QuestionRenderer } from "./QuestionRenderer";
import { convertMathJax, typesetMath } from "modules/assessment/AssessmentApp";

export const QuestionPaper = ({ questionsData, subjectName }) => {
  const paperRef = useRef(null);

  useEffect(() => {
    if (typeof typesetMath === 'function' && paperRef.current) {
      typesetMath(paperRef.current);
    }
  }, [questionsData]);

  const handlePrint = (withAnswers) => {
    // Clone to avoid modifying the actual screen DOM
    const printContent = document.querySelector(".question-paper-container").cloneNode(true);

    // Toggle inline answers visibility based on print mode
    const answers = printContent.querySelectorAll(".answer-inline");
    answers.forEach(el => {
      if (withAnswers) {
        el.classList.remove("hidden");
        el.style.display = "block";
      } else {
        el.style.display = "none";
      }
    });

    const styles = Array.from(document.styleSheets)
      .map((sheet) => {
        try {
          return Array.from(sheet.cssRules)
            .map((rule) => rule.cssText)
            .join("\n");
        } catch {
          return "";
        }
      })
      .join("\n");

    const printWindow = window.open("", "", "width=800,height=600");
    printWindow.document.write("<html><head><title>Print</title>");
    printWindow.document.write(`
      <style>
        ${styles}
        /* Hide default browser header and footer */
        @page { size: auto; margin: 0mm; }
        @media print {
          html, body { overflow: visible !important; }
          body { padding: 15mm !important; margin: 0 !important; font-family: "Times New Roman", Times, serif; color: #000; background: #fff; }
          .question-paper-container { padding: 0 !important; margin: 0 !important; }
          .question-item { page-break-inside: avoid; margin-bottom: 20px; }
          /* Ensure no scrollbars are printed */
          ::-webkit-scrollbar { display: none; }
        }
      </style>
    `);
    printWindow.document.write("</head><body class='bg-white text-black'>");
    
    // Inject the formal Header ONLY for printing
    printWindow.document.write(`
      <div style="text-align: center; margin-bottom: 30px; font-family: 'Times New Roman', Times, serif;">
        <h1 style="font-size: 20px; font-weight: bold; margin: 0 0 10px 0; text-transform: uppercase;">${subjectName || "EXAMINATION PAPER"}</h1>
        <p style="font-size: 14px; margin: 0 0 5px 0;"><strong>Time Allowed:</strong> 3 Hours &nbsp;&nbsp;&nbsp;&nbsp; <strong>Maximum Marks:</strong> 100</p>
        <hr style="border: 0; border-top: 1px solid #000; margin: 15px 0;" />
        <p style="font-size: 14px; text-align: left; font-style: italic; margin: 0;"><strong>Note:</strong> All questions are compulsory. Read the questions carefully and choose the correct option.</p>
        <hr style="border: 0; border-top: 1px solid #000; margin: 15px 0 25px 0;" />
      </div>
    `);
    
    printWindow.document.write(printContent.outerHTML);
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="relative">
      {/* Print Options Toolbar */}
      <div className="flex justify-end gap-3 mb-6">
        <button
          onClick={() => handlePrint(false)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition shadow-sm text-sm font-semibold"
        >
          <PrintIcon fontSize="small" /> Print Questions
        </button>
        <button
          onClick={() => handlePrint(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-800/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 rounded-lg transition shadow-sm text-sm font-semibold"
        >
          <PrintIcon fontSize="small" /> Print with Answers
        </button>
      </div>

      {/* The Printable Container - Screen View designed to look like A4 Paper */}
      <div ref={paperRef} className="question-paper-container font-serif bg-white text-black max-w-[210mm] mx-auto p-10 sm:p-16 shadow-2xl border border-gray-200 leading-relaxed print:shadow-none print:border-none print:p-0">
        <div className="space-y-6">
          {questionsData?.map((question, index) => {
            const sortedChoices = question?.answerChoices?.slice().sort((a, b) => (a.id || 0) - (b.id || 0));
            const correctChoice = question?.answerChoices?.find(c => c.isCorrect);
            const isOpenEnded = question?.questionType === "OPEN_ENDED" || 
                                question?.questionType === "openended" ||
                                question?.questionType === "FILL_IN_THE_BLANK" || 
                                question?.questionType === "MATHS_CALCULATION";

            return (
              <div key={question.id || question.code || index} className="question-item text-base">
                <div className="mb-2">
                  <QuestionRenderer question={question} index={index} isPaperView={true} />
                </div>

                {sortedChoices?.length > 0 && !isOpenEnded && (
                  <div className="ml-6 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                    {sortedChoices.map((choice, choiceIndex) => (
                      <div key={choice.id || choiceIndex} className="flex items-start">
                        <span className="mr-2 whitespace-nowrap">
                          ({String.fromCharCode(97 + choiceIndex)})
                        </span>
                        <span>
                          {typeof convertMathJax === 'function'
                            ? convertMathJax(choice.choiceText)
                            : choice.choiceText}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Inline Answer - Hidden on screen, shown when printing with answers */}
                <div className="answer-inline hidden ml-6 mt-3 text-sm font-bold italic">
                  Answer: 
                  <span className="ml-2 font-normal not-italic">
                    {!isOpenEnded && (
                      `(${String.fromCharCode(97 + (sortedChoices?.findIndex(c => c.isCorrect) > -1 ? sortedChoices.findIndex(c => c.isCorrect) : 0))}) `
                    )}
                    {correctChoice?.choiceText ? (
                      typeof convertMathJax === 'function'
                        ? convertMathJax(correctChoice.choiceText)
                        : correctChoice.choiceText
                    ) : "N/A"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default QuestionPaper;
